/** Alegerea din pasul Fațada, fără React: ce se întâmplă la un clic pe o
 *  imagine și ce i se spune omului despre ce a ales.
 *
 *  Regula e una: între zero și trei referințe, în ordinea alegerii, din orice
 *  stiluri. Fiecare poate deveni o variantă; una singură înseamnă că toate
 *  variantele o poartă. Omului i se spune rezultatul («toate variantele vor
 *  avea stilul X»), nu mecanismul.
 */
import type { AlegereFatada, ReferintaFatada, SistemFatada } from "../dosarclient";
import { IMAGINI, RECOMANDATE, type Stil } from "./catalog";

export const MAX_REFERINTE = 3;

export const ALEGERE_GOALA: AlegereFatada = { sistem: null, referinte: [] };

const STIL_IMAGINII = new Map(IMAGINI.map((i) => [i.id, i.stil]));

/** Sistemul pe care îl preselectează standardul clădirii (pasul 3).
 *
 *  Treptele temei sunt patru, sistemele trei: bugetul cade pe termosistem,
 *  standardul pe termosistem cu placări, premium și lux pe fațada ventilată. */
export function sistemDinStandard(nivel: string | null | undefined): SistemFatada {
  if (nivel === "buget") return "etics";
  if (nivel === "premium" || nivel === "lux") return "ventilata";
  return "etics_placari";
}

/** Imaginile catalogului în ordinea potrivirii cu nivelul clădirii: întâi cele
 *  de pe nivelul ales, apoi cele de pe niveluri vecine, cele fără nivel la un pas,
 *  restul la urmă — fără să se ascundă vreuna (o imagine de lux pe buget rămâne
 *  o inspirație, agentul o adaptează). Ordinea dinăuntrul unei trepte rămâne. */
const ORDINE_NIVEL = ["buget", "standard", "premium", "lux"];
export function laNivel<T extends { nivel?: string }>(imagini: T[], nivel: string | null | undefined): T[] {
  const n = ORDINE_NIVEL.indexOf(nivel ?? "");
  if (n < 0) return imagini;
  const pas = (i: T) => { const k = ORDINE_NIVEL.indexOf(i.nivel ?? ""); return k < 0 ? 1 : Math.abs(k - n); };
  return imagini.map((i, k) => ({ i, k, p: pas(i) })).sort((a, b) => a.p - b.p || a.k - b.k).map((x) => x.i);
}

export function dinCatalog(id: string): ReferintaFatada {
  return { sursa: "catalog", id, stil: STIL_IMAGINII.get(id) ?? "" };
}

/** Cele trei referințe puse când omul pornește fără să aleagă nimic. */
export function alegereaNoastra(): ReferintaFatada[] {
  return RECOMANDATE.slice(0, MAX_REFERINTE).map(dinCatalog);
}

export function pozitia(refs: ReferintaFatada[], id: string): number {
  return refs.findIndex((r) => r.sursa === "catalog" && r.id === id);
}

/** Un clic pe o imagine din catalog: o scoate dacă era aleasă, altfel o
 *  adaugă la coadă. Cu trei deja alese nu se schimbă nimic și `plin` spune de
 *  ce — o a patra NU o înlocuiește pe cea mai veche fără să întrebe. */
export function comuta(refs: ReferintaFatada[], id: string):
    { refs: ReferintaFatada[]; plin: boolean } {
  const k = pozitia(refs, id);
  if (k >= 0) return { refs: refs.filter((_, i) => i !== k), plin: false };
  if (refs.length >= MAX_REFERINTE) return { refs, plin: true };
  return { refs: [...refs, dinCatalog(id)], plin: false };
}

/** `poza` e id-ul pozei deja urcate (`urcaPozaFatada`), nu imaginea. */
export function adaugaPoza(refs: ReferintaFatada[], poza: string, nume: string):
    { refs: ReferintaFatada[]; plin: boolean } {
  if (refs.length >= MAX_REFERINTE) return { refs, plin: true };
  return { refs: [...refs, { sursa: "incarcata", poza, nume }], plin: false };
}

/** Ce iese din referințe, ca omul să citească urmarea alegerii lui.
 *
 *  Două imagini din același stil dau tot «un stil»: variantele diferă, dar
 *  stilul e unul. Fiecare poză încărcată e o direcție a ei. */
export type Rezumat =
  | { fel: "gol" }
  | { fel: "un_stil"; stil: Stil }
  | { fel: "o_poza" }
  | { fel: "mai_multe"; parti: (Stil | "poza")[] };

export function rezumat(refs: ReferintaFatada[]): Rezumat {
  if (!refs.length) return { fel: "gol" };
  const parti: (Stil | "poza")[] = [];
  for (const r of refs) {
    if (r.sursa === "incarcata") parti.push("poza");
    else if (!parti.includes(r.stil as Stil)) parti.push(r.stil as Stil);
  }
  if (parti.length === 1) return parti[0] === "poza" ? { fel: "o_poza" } : { fel: "un_stil", stil: parti[0] };
  return { fel: "mai_multe", parti };
}

/** Micșorează o poză încărcată până la `latura` pixeli pe latura lungă și o
 *  întoarce ca JPEG în data URL. Fără ea, o fotografie de telefon de 5 MB ar
 *  sta întreagă în temă. */
export function micsoreaza(fisier: File, latura = 900): Promise<string> {
  return new Promise((ok, esec) => {
    const url = URL.createObjectURL(fisier);
    const img = new Image();
    img.onload = () => {
      const k = Math.min(1, latura / Math.max(img.width, img.height));
      const c = document.createElement("canvas");
      c.width = Math.round(img.width * k);
      c.height = Math.round(img.height * k);
      c.getContext("2d")?.drawImage(img, 0, 0, c.width, c.height);
      URL.revokeObjectURL(url);
      ok(c.toDataURL("image/jpeg", 0.8));
    };
    img.onerror = () => { URL.revokeObjectURL(url); esec(new Error("imagine")); };
    img.src = url;
  });
}
